<template lang="pug">
component(:is="layoutComponent" :showLogo="true" :noPadding="true")
  .ticket-checkout-page(:class="{ 'is-empty-cart': isEmptyCart, 'has-checkout-footer': !isEmptyCart && !pageError }")
    .checkout-container(v-if="!pageError" :class="{ 'checkout-container--empty': isEmptyCart }")
      //- Empty Cart State
      .empty-cart-section(v-if="isEmptyCart")
        .empty-cart-icon
          img(src="@/assets/icons/shopping-cart.svg" alt="Empty cart")
        h2.empty-cart-title Your cart is empty
        p.empty-cart-text Add tickets or items to your cart to get started
        el-button(
          type="primary"
          size="large"
          @click="router.push('/web/dashboard')"
        ).browse-btn Browse Marketplace

      //- Order Summary (only show if cart has items)
      .checkout-section.order-summary-section(v-if="lines.length > 0")
        .section-header
          h2.section-title Order Summary
          button.clear-cart-btn(
            v-if="!hasPaymentIntent"
            type="button"
            @click="clearCart"
            title="Clear cart"
            aria-label="Clear cart"
          )
            img(src="@/assets/icons/trash.svg" alt="")
            span Clear cart

        //- Multiple creators warning
        .multi-creator-notice(v-if="creatorGroups.length > 1")
          span.notice-icon ⓘ
          span.notice-text You have items from {{ creatorGroups.length }} creators. Select one creator to checkout at a time.

        //- Creator groups
        .creator-groups
          .creator-group(
            v-for="group in creatorGroups"
            :key="group.creatorId"
            :class="{ active: selectedCreatorId === group.creatorId, 'is-selectable': creatorGroups.length > 1 }"
            @click="creatorGroups.length > 1 && selectCreatorForCheckout(group.creatorId)"
            :role="creatorGroups.length > 1 ? 'button' : undefined"
            :tabindex="creatorGroups.length > 1 ? 0 : undefined"
            @keydown.enter="creatorGroups.length > 1 && selectCreatorForCheckout(group.creatorId)"
          )
            .creator-header
              .creator-info
                span.creator-checkbox(
                  v-if="creatorGroups.length > 1"
                  :class="{ checked: selectedCreatorId === group.creatorId }"
                  aria-hidden="true"
                )
                  img(
                    v-if="selectedCreatorId === group.creatorId"
                    src="@/assets/icons/check.svg"
                    alt=""
                  )
                span.creator-name {{ group.creatorTitle }}
              span.creator-item-count {{ getGroupItemCount(group) }} {{ getGroupItemCount(group) === 1 ? 'item' : 'items' }}

            //- Items in this group
            .creator-items(@click.stop)
              .line-item(v-for="line in group.lines" :key="line.id")
                template(v-if="line.lineType === 'Ticket'")
                  .line-item-info
                    .line-title {{ line.variantTitle || 'Ticket' }}
                    .line-option(v-if="line.selectedOption") Option: {{ line.selectedOption }}
                  .line-item-right
                    .qty-stepper(v-if="canAdjustQuantity(line)")
                      button.qty-btn(
                        type="button"
                        @click="decrementLineQuantity(line)"
                        :aria-label="`Decrease ${line.variantTitle || 'ticket'} quantity`"
                      )
                        img(src="@/assets/icons/minus.svg" alt="")
                      span.qty-value {{ getLineQuantity(line) }}
                      button.qty-btn.plus(
                        type="button"
                        @click="incrementLineQuantity(line)"
                        :disabled="getLineQuantity(line) >= maxCartQuantity"
                        :aria-label="`Increase ${line.variantTitle || 'ticket'} quantity`"
                      )
                        img(src="@/assets/icons/plus.svg" alt="")
                    .line-price(v-if="line.isFree") Free
                    .line-price(v-else) ${{ ((line.unitCost || 0) * (line.quantity || 1)).toFixed(2) }}
                    button.remove-btn(@click="removeLine(line.id)" title="Remove item")
                      img(src="@/assets/icons/close.svg" alt="Remove")

                template(v-else-if="line.lineType === 'Donation'")
                  .line-item-info
                    .line-title Donation
                    .line-option(v-if="line.donationCauseIds && line.donationCauseIds.length > 1")
                      | {{ line.donationCauseIds.length }} causes × ${{ (line.donationAmount || 0).toFixed(2) }}
                  .line-item-right
                    .line-price ${{ ((line.donationAmount || 0) * (line.donationCauseIds?.length || 1)).toFixed(2) }}
                    button.remove-btn(@click="removeLine(line.id)" title="Remove item")
                      img(src="@/assets/icons/close.svg" alt="Remove")

                template(v-else-if="line.lineType === 'Sticker'")
                  .line-item-info
                    .line-title {{ line.variantTitle || 'Sticker' }}
                    .line-option(v-if="line.donationCauseIds && line.donationCauseIds.length > 1")
                      | {{ line.donationCauseIds.length }} causes × ${{ (line.unitCost || 0).toFixed(2) }}
                    .line-option(v-else-if="line.donationCauseId")
                      | Donation
                    .line-option(v-else-if="line.sweepstakesEntryCost")
                      | Sweepstakes entry: ${{ (line.sweepstakesEntryCost || 0).toFixed(2) }}
                  .line-item-right
                    .qty-stepper(v-if="canAdjustQuantity(line)")
                      button.qty-btn(
                        type="button"
                        @click="decrementLineQuantity(line)"
                        :aria-label="`Decrease ${line.variantTitle || 'sticker'} quantity`"
                      )
                        img(src="@/assets/icons/minus.svg" alt="")
                      span.qty-value {{ getLineQuantity(line) }}
                      button.qty-btn.plus(
                        type="button"
                        @click="incrementLineQuantity(line)"
                        :disabled="getLineQuantity(line) >= maxCartQuantity"
                        :aria-label="`Increase ${line.variantTitle || 'sticker'} quantity`"
                      )
                        img(src="@/assets/icons/plus.svg" alt="")
                    .line-price
                      | ${{ ((line.unitCost || 0) * (line.quantity || 1) * (line.donationCauseIds?.length || 1)).toFixed(2) }}
                    button.remove-btn(@click="removeLine(line.id)" title="Remove item")
                      img(src="@/assets/icons/close.svg" alt="Remove")

                template(v-else)
                  .line-item-info
                    .line-title {{ line.lineType }}
                  .line-item-right
                    .line-price(v-if="line.unitCost") ${{ (line.unitCost || 0).toFixed(2) }}
                    .line-price(v-else) Included
                    button.remove-btn(@click="removeLine(line.id)" title="Remove item")
                      img(src="@/assets/icons/close.svg" alt="Remove")

            //- Group subtotal
            .creator-footer
              .creator-subtotal
                span.subtotal-label Subtotal
                span.subtotal-amount ${{ calculateGroupSubtotal(group).toFixed(2) }}

      //- Simple order summary for payment intent mode
      .checkout-section(v-else-if="hasPaymentIntent && serverAmount !== null")
        h2.section-title Order Summary
        .fee-row.fee-total
          span Total Amount
          span ${{ (serverAmount / 100).toFixed(2) }}

      //- Contact Info (anonymous users only, not for payment intents with email)
      .checkout-section.contact-section(
        v-if="!isEmptyCart && !isAuthenticated && !hasPaymentIntent"
      )
        h2.section-title Contact Information
        el-form(
          ref="contactFormRef"
          :model="contactInfo"
          :rules="contactRules"
          label-position="top"
        )
          .form-row
            el-form-item(label="First Name" prop="firstName").form-col
              el-input(v-model="contactInfo.firstName" placeholder="First name")
            el-form-item(label="Last Name" prop="lastName").form-col
              el-input(v-model="contactInfo.lastName" placeholder="Last name")
          el-form-item(label="Email" prop="email")
            el-input(v-model="contactInfo.email" type="email" placeholder="your@email.com")
          el-form-item(label="Phone (optional)" prop="phone")
            el-input(
              v-model="contactInfo.phone"
              type="tel"
              placeholder="(123) 456-7890"
            )

      //- Giving Wallet
      .checkout-section.wallet-section(v-if="!isEmptyCart && isAuthenticated && hasWallet && walletEligibleTotal > 0")
        .wallet-toggle
          el-checkbox(v-model="useGivingWallet")
            | Use Giving Wallet credit (up to ${{ maxWalletCredit.toFixed(2) }})
          .wallet-info-text(v-if="walletEligibleTotal < subtotal")
            | Only donation portions are wallet-eligible
          .wallet-note(v-if="useGivingWallet")
            span Applied: ${{ Math.min(givingWalletAmount, maxWalletCredit).toFixed(2) }}
            span &nbsp;·&nbsp;
            span Remaining charge: ${{ chargeAfterWallet.toFixed(2) }}

      //- Fee Breakdown (only show if cart has items)
      .checkout-section(v-if="lines.length > 0")
        h2.section-title Price Breakdown
        .fee-row
          span Subtotal
          span ${{ subtotal.toFixed(2) }}
        .fee-row(v-if="subtotal > 0")
          span Platform fee (2%)
          span ${{ fees.convenienceFee.toFixed(2) }}
        .fee-row(v-if="subtotal > 0")
          span Credit card fee (2.9% + $0.30)
          span ${{ fees.creditCardFee.toFixed(2) }}
        .fee-row(v-if="useGivingWallet && givingWalletAmount > 0")
          span.text-success Giving Wallet credit
          span.text-success -${{ Math.min(givingWalletAmount, maxWalletCredit).toFixed(2) }}
        .fee-row.fee-total
          span Total
          span(v-if="serverAmount !== null") ${{ (serverAmount / 100).toFixed(2) }}
          span(v-else-if="subtotal === 0") FREE
          span(v-else) ${{ totalWithFees.toFixed(2) }}

      //- Opt-in notifications
      .checkout-section.optin-section(v-if="!isEmptyCart")
        el-checkbox(v-model="optInNotifications")
          | Keep me informed about this event

      //- Stripe Payment Element
      .checkout-section.payment-section(v-if="paymentMounted")
        h2.section-title Payment
        #payment-element

    //- Fixed bottom checkout actions
    .checkout-footer(v-if="!isEmptyCart && !pageError")
      .checkout-footer-inner
        .checkout-footer-actions
          el-button(
            v-if="!paymentMounted"
            type="primary"
            :loading="loading"
            :disabled="loading"
            @click="initiateCheckout"
            size="large"
          ).checkout-btn
            span {{ subtotal === 0 ? 'Get Free Tickets' : 'Proceed to Payment' }}

          el-button(
            v-if="paymentMounted"
            type="primary"
            :loading="loading"
            :disabled="loading || !paymentReady"
            @click="confirmPayment"
            size="large"
          ).checkout-btn
            span Pay ${{ serverAmount !== null ? (serverAmount / 100).toFixed(2) : '...' }}

          button.cancel-btn(type="button" @click="goBack") Cancel

    .page-error(v-if="pageError")
      p {{ pageError }}
      el-button(type="primary" @click="router.push('/web')") Back to Home
</template>

<script lang="ts">
import {
  computed, defineComponent, nextTick, onMounted, reactive, ref, watch,
} from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import { ElLoading, ElMessageBox } from 'element-plus';
import WebAppLayout from '../dashboard/WebAppLayout.vue';
import { makeRequest } from '@/graphs/index';
import { createCartCheckoutIntent, benevityDetails, findMyTickets } from '@/graphs/gql';
import type { CartLine, CartCreatorGroup } from '@/store/modules/cart';
import globalShared from '@/shared';
import webAppToast from '@/utils/webAppToast';
import {
  appendEmbedOrganizationQuery,
  captureEmbedOrganizationContext,
  isEmbeddedContext,
  navigateToEmbedOrganizationHome,
} from '@/utils/embeddedUtils';
import {
  countAllExpectedTickets,
  formatCheckoutErrorMessage,
  getTicketStickerIds,
  saveGuestCheckoutContact,
  saveTicketCheckoutContext,
} from '@/utils/ticketDisplay';
import { ticketLog } from '@/utils/ticketLogger';

interface ContactInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

function calculateFinalPayment(subtotalAmt: number) {
  if (subtotalAmt === 0) return { convenienceFee: 0, creditCardFee: 0 };
  const convenienceFee = Math.min(parseFloat((0.02 * subtotalAmt).toFixed(2)), 30);
  const totalWithConvenience = subtotalAmt + convenienceFee;
  const total = parseFloat(((totalWithConvenience + 0.30) / (1 - 0.029)).toFixed(2));
  const creditCardFee = parseFloat((total - totalWithConvenience).toFixed(2));
  return { convenienceFee, creditCardFee };
}

function loadStripeScript(callback: () => void) {
  const existing = document.querySelector('script[src*="js.stripe.com"]');
  if (existing) {
    if ((window as any).Stripe) { callback(); } else { existing.addEventListener('load', callback); }
    return;
  }
  const script = document.createElement('script');
  script.src = 'https://js.stripe.com/v3/';
  script.addEventListener('load', callback);
  document.head.appendChild(script);
}

export default defineComponent({
  name: 'TicketCheckout',
  components: { WebAppLayout },
  setup() {
    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const { getMobileOperatingSystem, isSafari, handleSafariPrompt } = globalShared();

    // Mobile webview opens /payment/intent/cart/:cartId; web opens /web/checkout
    const isMobileAppCheckout = computed(() => {
      const isCartIntent = route.name === 'CartPaymentIntent';
      return isCartIntent;
    });

    // Check if this is a payment intent from backend (mobile flow)
    // For free checkouts, client_secret is optional
    const hasPubkey = !!route.query.pubkey;
    const hasClientSecret = !!route.query.client_secret;
    const isFree = route.query.free === 'true';
    const hasPaymentIntent = !!(route.query.pubkey && (route.query.client_secret || route.query.free === 'true'));
    const cartId = route.params.cartId as string;
    
    // Use plain div wrapper when payment intent (parent route provides layout)
    const layoutComponent = computed(() => {
      const component = hasPaymentIntent ? 'div' : 'web-app-layout';
      return component;
    });

    const lines = computed<CartLine[]>(() => store.getters['cart/cartLines']);
    const creatorGroups = computed<CartCreatorGroup[]>(() => store.getters['cart/creatorGroups']);
    const isEmptyCart = computed(() => !hasPaymentIntent && lines.value.length === 0);

    const contactInfo = reactive<ContactInfo>({
      firstName: '', lastName: '', email: '', phone: '',
    });
    const contactFormRef = ref();

    // Multi-creator cart support
    const selectedCreatorId = ref<string | null>(null);

    // Auto-select creator if only one
    watch(creatorGroups, (groups) => {
      if (groups.length === 1) {
        selectedCreatorId.value = groups[0].creatorId;
      } else if (groups.length === 0) {
        selectedCreatorId.value = null;
      }
    }, { immediate: true });

    const me = computed(() => {
      const user = store.getters['users/me'];
      return user;
    });
    const isAuthenticated = computed(() => {
      const auth = !!me.value;
      return auth;
    });

    watch(me, (user) => {
      if (user) {
        contactInfo.email = user.email || '';
        contactInfo.firstName = user.firstname || '';
        contactInfo.lastName = user.lastname || '';
      }
    }, { immediate: true });

    const contactRules = {
      firstName: [{ required: true, message: 'First name is required', trigger: 'blur' }],
      lastName: [{ required: true, message: 'Last name is required', trigger: 'blur' }],
      email: [
        { required: true, message: 'Email is required', trigger: 'blur' },
        { type: 'email', message: 'Enter a valid email', trigger: 'blur' },
      ],
    };

    const subtotal = computed(() => {
      // Filter by selected creator for multi-creator checkout
      const checkoutLines = selectedCreatorId.value
        ? lines.value.filter((l) => l.creatorId === selectedCreatorId.value)
        : lines.value;

      return checkoutLines.reduce((sum, line) => {
        if (line.lineType === 'Ticket') {
          return sum + (line.isFree ? 0 : (line.unitCost ?? 0) * (line.quantity ?? 1));
        }
        if (line.lineType === 'Donation') {
          const amount = line.donationAmount ?? 0;
          const causeCount = line.donationCauseIds?.length ?? 1;
          return sum + (amount * causeCount);
        }
        if (line.lineType === 'Sticker') {
          const amount = (line.unitCost ?? 0) * (line.quantity ?? 1);
          const causeCount = line.donationCauseIds?.length ?? 1;
          return sum + (amount * causeCount);
        }
        return sum + (line.unitCost ?? 0);
      }, 0);
    });

    const fees = computed(() => calculateFinalPayment(subtotal.value));

    const totalWithFees = computed(() => {
      if (subtotal.value === 0) return 0;
      return parseFloat(
        (subtotal.value + fees.value.convenienceFee + fees.value.creditCardFee).toFixed(2),
      );
    });

    const walletBalance = ref(0);
    const hasWallet = ref(false);
    const useGivingWallet = ref(false);

    const donationTotal = computed(() => {
      // Filter by selected creator for multi-creator checkout
      const checkoutLines = selectedCreatorId.value
        ? lines.value.filter((l) => l.creatorId === selectedCreatorId.value)
        : lines.value;

      return checkoutLines
        .filter((l) => l.lineType === 'Donation')
        .reduce((sum, l) => {
          const amount = l.donationAmount ?? 0;
          const causeCount = l.donationCauseIds?.length ?? 1;
          return sum + (amount * causeCount);
        }, 0);
    });

    const walletEligibleTotal = computed(() => {
      // Filter by selected creator for multi-creator checkout
      const checkoutLines = selectedCreatorId.value
        ? lines.value.filter((l) => l.creatorId === selectedCreatorId.value)
        : lines.value;

      let total = 0;

      for (const line of checkoutLines) {
        // Donation add-ons are always wallet-eligible
        if (line.lineType === 'Donation') {
          const amount = line.donationAmount ?? 0;
          const causeCount = line.donationCauseIds?.length ?? 1;
          total += amount * causeCount;
          continue;
        }

        // Campaign tickets (ticket lines with donationCauseId) are wallet-eligible
        if (line.lineType === 'Ticket' && line.donationCauseId) {
          const amount = (line.unitCost ?? 0) * (line.quantity ?? 1);
          total += amount;
          continue;
        }

        // Sticker lines: Campaign stickers are wallet-eligible, MatchCampaign are NOT
        if (line.lineType === 'Sticker') {
          const campaignType = line.campaignType?.toLowerCase();
          if (campaignType === 'campaign') {
            // Campaign stickers are wallet-eligible
            const amount = (line.unitCost ?? 0) * (line.quantity ?? 1);
            const causeCount = line.donationCauseIds?.length ?? 1;
            total += amount * causeCount;
          }
          // MatchCampaign and other sticker types are NOT wallet-eligible
          continue;
        }
      }

      return total;
    });

    const maxWalletCredit = computed(() => Math.min(walletBalance.value, walletEligibleTotal.value));
    const givingWalletAmount = computed(() => (useGivingWallet.value ? maxWalletCredit.value : 0));
    const chargeAfterWallet = computed(() => Math.max(0, totalWithFees.value - givingWalletAmount.value));

    const optInNotifications = ref(true);

    const serverAmount = ref<number | null>(null);
    const checkoutOrderId = ref('');
    const loading = ref(false);
    const stripeError = ref<string | null>(null);
    const pageError = ref<string | null>(null);
    const paymentMounted = ref(false);
    const paymentReady = ref(false);

    let stripe: any = null;
    let elements: any = null;

    async function fetchWalletBalance() {
      try {
        const response = await makeRequest('query', benevityDetails, {}, { token: me.value?.token });
        if (response?.data?.benevityDetails) {
          walletBalance.value = parseFloat(response.data.benevityDetails.amountInWallet || 0);
          hasWallet.value = walletBalance.value > 0;
        }
      } catch (e) {
        console.error('Wallet fetch error:', e);
      }
    }

    // Calculate subtotal for a creator group
    function calculateGroupSubtotal(group: CartCreatorGroup): number {
      return group.lines.reduce((sum, line) => {
        if (line.lineType === 'Ticket') {
          return sum + (line.isFree ? 0 : (line.unitCost ?? 0) * (line.quantity ?? 1));
        }
        if (line.lineType === 'Donation') {
          const amount = line.donationAmount ?? 0;
          const causeCount = line.donationCauseIds?.length ?? 1;
          return sum + (amount * causeCount);
        }
        if (line.lineType === 'Sticker') {
          const amount = (line.unitCost ?? 0) * (line.quantity ?? 1);
          const causeCount = line.donationCauseIds?.length ?? 1;
          return sum + (amount * causeCount);
        }
        return sum + (line.unitCost ?? 0);
      }, 0);
    }

    function findScrollContainer(el: HTMLElement): HTMLElement | null {
      let parent = el.parentElement;
      while (parent && parent !== document.body) {
        const { overflowY } = window.getComputedStyle(parent);
        if (
          (overflowY === 'auto' || overflowY === 'scroll')
          && parent.scrollHeight > parent.clientHeight
        ) {
          return parent;
        }
        parent = parent.parentElement;
      }
      return null;
    }

    function scrollToCheckoutSection(target: 'contact' | 'order' | 'payment') {
      const selectorMap = {
        contact: '.contact-section',
        order: '.order-summary-section',
        payment: '.payment-section',
      };

      nextTick(() => {
        nextTick(() => {
          const root = document.querySelector('.ticket-checkout-page');
          const selector = selectorMap[target];
          const el = root?.querySelector(selector) as HTMLElement | null;
          if (!el) return;

          const rect = el.getBoundingClientRect();
          const scrollContainer = findScrollContainer(el);
          const offset = 16;

          if (scrollContainer) {
            const containerRect = scrollContainer.getBoundingClientRect();
            const nextTop = scrollContainer.scrollTop + rect.top - containerRect.top - offset;
            scrollContainer.scrollTo({ top: Math.max(0, nextTop), behavior: 'smooth' });
            return;
          }

          const nextTop = window.scrollY + rect.top - offset;
          window.scrollTo({ top: Math.max(0, nextTop), behavior: 'smooth' });
        });
      });
    }

    // Select a creator for checkout
    function selectCreatorForCheckout(creatorId: string) {
      selectedCreatorId.value = creatorId;
    }

    function buildLines() {
      // Filter lines by selected creator for multi-creator checkout
      const checkoutLines = selectedCreatorId.value
        ? lines.value.filter((l) => l.creatorId === selectedCreatorId.value)
        : lines.value;

      const builtLines = checkoutLines.map((line) => {
        const out: any = {
          stickerId: line.stickerId,
          lineType: line.lineType,
        };
        if (line.variantId) out.variantId = line.variantId;
        if (line.quantity) out.quantity = line.quantity;
        if (line.selectedOption) out.selectedOption = line.selectedOption;
        if (line.addOnId) out.addOnId = line.addOnId;

        if (line.sweepstakesEntries) out.sweepstakesEntries = line.sweepstakesEntries;
        
        // Multi-cause support: donationCauseIds takes precedence
        if (line.donationCauseIds && line.donationCauseIds.length > 0) {
          out.donationCauseIds = line.donationCauseIds;
          
          // For Sticker lines with multiple causes, backend needs donationAmount
          // This is the per-cause amount that backend will multiply by cause count
          if (line.lineType === 'Sticker' && line.unitCost) {
            out.donationAmount = line.unitCost * (line.quantity || 1);
          }
        } else if (line.donationCauseId) {
          out.donationCauseId = line.donationCauseId;

          // Single-cause campaign stickers need donationAmount for backend validation
          if (line.lineType === 'Sticker' && line.unitCost) {
            out.donationAmount = line.unitCost * (line.quantity || 1);
          }
        }
        
        if (line.donationAmount && !out.donationAmount) {
          out.donationAmount = line.donationAmount;
        }
        
        // V2 new fields from TICKET-1
        if (line.sweepstakesEntryCost) out.sweepstakesEntryCost = line.sweepstakesEntryCost;
        if (line.socialShopId) out.socialShopId = line.socialShopId;
        if (line.glowId) out.glowId = line.glowId;
        
        return out;
      });

      return builtLines;
    }

    function showCheckoutError(message: string) {
      webAppToast.error(formatCheckoutErrorMessage(message, lines.value));
    }

    async function loadStripeAndMount(pubKey: string, clientSecret: string) {
      return new Promise<void>((resolve, reject) => {
        loadStripeScript(async () => {
          try {
            stripe = (window as any).Stripe(pubKey);
            elements = stripe.elements({ clientSecret });
            const paymentElement = elements.create('payment');

            paymentMounted.value = true;
            await nextTick();

            paymentElement.mount('#payment-element');
            paymentElement.on('change', (e: any) => {
              paymentReady.value = e.complete;
              stripeError.value = e.error ? e.error.message : null;
            });
            resolve();
          } catch (err) {
            reject(err);
          }
        });
      });
    }

    async function validateGuestContact(): Promise<boolean> {
      if (isAuthenticated.value) return true;

      if (hasPaymentIntent) {
        const email = me.value?.email || contactInfo.email;
        if (!email) {
          webAppToast.warning('A valid email is required to link your purchase to your account.');
          return false;
        }
        return true;
      }

      try {
        await contactFormRef.value?.validate();
      } catch {
        webAppToast.warning('Please complete the contact information.');
        scrollToCheckoutSection('contact');
        return false;
      }

      return true;
    }

    async function initiateCheckout() {
      // If payment intent already provided (backend created it), skip to payment
      if (hasPaymentIntent) {
        if (!(await validateGuestContact())) return;
        // Payment form already ready, user can proceed to confirmPayment
        return;
      }

      if (!(await validateGuestContact())) return;

      if (!isAuthenticated.value && contactInfo.email) {
        saveGuestCheckoutContact(contactInfo.email, contactInfo.phone || '');
      }

      // Validate creator selection for multi-creator cart
      if (creatorGroups.value.length > 1 && !selectedCreatorId.value) {
        webAppToast.error('Please select a creator to checkout.');
        return;
      }

      // Check if selected creator has any lines
      const checkoutLines = selectedCreatorId.value
        ? lines.value.filter((l) => l.creatorId === selectedCreatorId.value)
        : lines.value;

      if (checkoutLines.length === 0) {
        webAppToast.error('No items to checkout.');
        scrollToCheckoutSection('order');
        return;
      }

      loading.value = true;
      try {
        const builtLines = buildLines();
        const input: any = {
          email: me.value?.email || contactInfo.email || '',
          lines: builtLines,
          convenienceFee: fees.value.convenienceFee,
          creditCardFee: fees.value.creditCardFee,
          optInNotifications: optInNotifications.value,
          useGivingWallet: useGivingWallet.value,
          givingWalletAmount: useGivingWallet.value ? givingWalletAmount.value : 0,
        };

        if (contactInfo.firstName) input.firstName = contactInfo.firstName;
        if (contactInfo.lastName) input.lastName = contactInfo.lastName;
        if (contactInfo.phone) input.phone = contactInfo.phone.replace(/[^\d]/g, '');
        if (me.value) {
          input.purchaseBy = 'User';
          input.userId = me.value.id;
        }

        const token = me.value?.token || null;

        const response = await makeRequest(
          'mutation',
          createCartCheckoutIntent,
          { input },
          token ? { token } : null,
        );

        if (response?.errors) {
          throw new Error(response.errors[0]?.message);
        }

        const raw = response.data.createCartCheckoutIntent;
        const decoded = JSON.parse(atob(raw));

        serverAmount.value = decoded.amount;

        const rawOrderId = decoded.checkout_order_id;
        if (typeof rawOrderId === 'string') {
          checkoutOrderId.value = rawOrderId;
        } else if (rawOrderId && typeof rawOrderId === 'object') {
          checkoutOrderId.value = rawOrderId.id || rawOrderId.orderId || rawOrderId.order_id || '';
        } else {
          checkoutOrderId.value = '';
        }

        if (decoded.free === true) {
          const stickerId = lines.value[0]?.stickerId || '';

          if (selectedCreatorId.value && creatorGroups.value.length > 1) {
            store.dispatch('cart/clearCreatorLines', selectedCreatorId.value);
          } else {
            store.dispatch('cart/clearCart');
          }

          completeCheckout({ stickerId, isFree: true });
          return;
        }

        await loadStripeAndMount(decoded.stripe_pub_key, decoded.client_secret);
        scrollToCheckoutSection('payment');
      } catch (err: any) {
        const errorMsg = err.message || '';

        if (errorMsg.toLowerCase().includes('stripe account')
            || (errorMsg.toLowerCase().includes('organizer')
              && errorMsg.toLowerCase().includes('connected'))) {
          webAppToast.error(
            'This organizer has not connected their payment account yet. '
            + 'Please contact the event organizer or try a different event.',
          );
        } else if (errorMsg.toLowerCase().includes('remaining')
                 || errorMsg.toLowerCase().includes('sold out')
                 || (errorMsg.toLowerCase().includes('ticket')
                   && errorMsg.toLowerCase().includes('available'))) {
          showCheckoutError(errorMsg);
        } else {
          showCheckoutError(errorMsg || 'Checkout failed. Please try again.');
        }
      } finally {
        loading.value = false;
      }
    }

    async function prepareCheckoutSuccess(stickerId: string) {
      const checkoutLines = selectedCreatorId.value
        ? lines.value.filter((line) => line.creatorId === selectedCreatorId.value)
        : lines.value;
      const ticketLines = checkoutLines.filter((line) => line.lineType === 'Ticket');
      const stickerIds = getTicketStickerIds(checkoutLines);
      const primaryStickerId = stickerId || stickerIds[0] || '';
      const expectedCount = countAllExpectedTickets(checkoutLines);

      ticketLog('Preparing checkout success context', {
        primaryStickerId,
        stickerIds,
        expectedCount,
        checkoutLineCount: checkoutLines.length,
        ticketLineCount: ticketLines.length,
        ticketLines: ticketLines.map((line) => ({
          stickerId: line.stickerId,
          variantId: line.variantId,
          variantTitle: line.variantTitle,
          quantity: line.quantity,
        })),
        orderId: checkoutOrderId.value,
      });

      let existingTicketIds: string[] = [];
      if (me.value?.token && stickerIds.length) {
        for (const sid of stickerIds) {
          try {
            const response = await makeRequest(
              'query',
              findMyTickets,
              { stickerId: sid },
              { token: me.value.token },
            );
            const ids = (response?.data?.findMyTickets || []).map(
              (ticket: { id: string }) => ticket.id,
            );
            existingTicketIds.push(...ids);
            ticketLog('Fetched existing tickets before checkout', { stickerId: sid, count: ids.length, ids });
          } catch (err) {
            ticketLog('Failed to fetch existing tickets before checkout', { stickerId: sid, err });
          }
        }
        existingTicketIds = [...new Set(existingTicketIds)];
      }

      const guestEmail = me.value?.email || contactInfo.email || '';
      const guestPhone = contactInfo.phone || '';
      const embedContext = captureEmbedOrganizationContext(route);

      if (!isAuthenticated.value && guestEmail) {
        saveGuestCheckoutContact(guestEmail, guestPhone);
      }

      saveTicketCheckoutContext({
        stickerId: primaryStickerId,
        stickerIds,
        orderId: checkoutOrderId.value,
        expectedCount,
        existingTicketIds,
        email: guestEmail || undefined,
        phone: guestPhone || undefined,
        embedOrganizationId: embedContext.organizationId || undefined,
        embedSource: embedContext.embedSource || undefined,
        cartLineSummary: checkoutLines.map((line) => ({
          lineType: line.lineType,
          stickerId: line.stickerId,
          stickerTitle: line.stickerTitle,
          variantId: line.variantId,
          variantTitle: line.variantTitle,
          quantity: line.quantity,
          unitCost: line.unitCost,
          donationAmount: line.donationAmount,
          isFree: line.isFree,
          donationCauseId: line.donationCauseId,
          donationCauseIds: line.donationCauseIds,
          sweepstakesEntries: line.sweepstakesEntries,
          sweepstakesEntryCost: line.sweepstakesEntryCost,
          campaignType: line.campaignType,
          selectedOption: line.selectedOption,
        })),
      });

      return expectedCount;
    }

    async function confirmPayment() {
      if (!stripe || !elements) return;
      stripeError.value = null;
      loading.value = true;

      try {
        const baseUrl = import.meta.env.VITE_WEB_BASE_URL || window.location.origin;
        const stickerId = lines.value[0]?.stickerId
          || (route.query.stickerId as string)
          || '';
        const expectedCount = await prepareCheckoutSuccess(stickerId);
        const stickerParam = stickerId ? `&stickerId=${stickerId}` : '';
        const ticketCountParam = expectedCount > 0 ? `&ticketCount=${expectedCount}` : '';
        const returnUrl = appendEmbedOrganizationQuery(
          `${baseUrl}/web/ticket/success?orderId=${checkoutOrderId.value}${stickerParam}${ticketCountParam}`,
          route,
        );

        const { error: stripeErr } = await stripe.confirmPayment({
          elements,
          confirmParams: {
            return_url: returnUrl,
            receipt_email: me.value?.email || contactInfo.email || undefined,
          },
          redirect: 'if_required',
        });

        if (stripeErr) {
          showCheckoutError(stripeErr.message || 'Payment failed. Please try again.');
          scrollToCheckoutSection('payment');
          return;
        }

        if (selectedCreatorId.value && creatorGroups.value.length > 1) {
          store.dispatch('cart/clearCreatorLines', selectedCreatorId.value);
        } else {
          store.dispatch('cart/clearCart');
        }

        if (isMobileAppCheckout.value) {
          const amount = serverAmount.value !== null
            ? serverAmount.value / 100
            : totalWithFees.value;
          redirectMobileApp(false, { stickerId, amount });
          return;
        }

        await redirectToWebSuccess({ stickerId, expectedCount });
      } finally {
        loading.value = false;
      }
    }

    async function redirectToWebSuccess({
      stickerId,
      isFree = false,
      expectedCount = 0,
    }: {
      stickerId: string;
      isFree?: boolean;
      expectedCount?: number;
    }) {
      // Check if more creators remain in cart
      const remainingGroups = store.getters['cart/creatorGroups'];
      
      if (remainingGroups.length > 0 && !isMobileAppCheckout.value) {
        // More creators remain - show success toast and reset selection
        webAppToast.success('Order placed! You have items from other creators remaining in cart.');
        selectedCreatorId.value = null;
        loading.value = false;
        paymentMounted.value = false;
        // Scroll back to top to show remaining creators
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      // No more creators - proceed to success page
      const stickerParam = stickerId ? `&stickerId=${stickerId}` : '';
      const freeParam = isFree ? '&free=true' : '';
      const ticketCountParam = expectedCount > 0 ? `&ticketCount=${expectedCount}` : '';
      const successPath = appendEmbedOrganizationQuery(
        `/web/ticket/success?orderId=${checkoutOrderId.value}${stickerParam}${freeParam}${ticketCountParam}`,
        route,
      );
      const baseUrl = import.meta.env.VITE_WEB_BASE_URL || window.location.origin;

      if (!isAuthenticated.value) {
        saveGuestCheckoutContact(contactInfo.email, contactInfo.phone || '');
        const completeProfilePath = appendEmbedOrganizationQuery(
          `/auth/web/complete-profile?email=${encodeURIComponent(contactInfo.email)}`
          + `&redirect=${encodeURIComponent(successPath)}`,
          route,
        );
        window.location.href = `${baseUrl}${completeProfilePath.startsWith('/') ? '' : '/'}${completeProfilePath}`;
        return;
      }

      router.push(successPath);
    }

    async function completeCheckout({
      stickerId,
      isFree = false,
      amount,
    }: {
      stickerId: string;
      isFree?: boolean;
      amount?: number;
    }) {
      const expectedCount = await prepareCheckoutSuccess(stickerId);

      if (isMobileAppCheckout.value) {
        redirectMobileApp(false, {
          stickerId,
          amount: amount ?? (serverAmount.value !== null ? serverAmount.value / 100 : totalWithFees.value),
        });
        return;
      }

      await redirectToWebSuccess({ stickerId, isFree, expectedCount });
    }

    function getRedirectIOSUrl() {
      return import.meta.env.VITE_APP_REDIRECT_URL;
    }

    async function redirectMobileApp(
      failed = false,
      context?: { stickerId?: string; amount?: number },
    ) {
      const loadingInstance = ElLoading.service({ lock: false });
      const isAndroid = getMobileOperatingSystem() === 'Android';
      const appId = import.meta.env.VITE_APP_ID;
      const appHost = import.meta.env.VITE_APP_HOST;
      const browserFallbackUrlBase = import.meta.env.VITE_WEB_BASE_URL;
      const browserFallbackUrlPath = 'web/dashboard';

      const stickerId = context?.stickerId
        ?? lines.value[0]?.stickerId
        ?? (route.query.stickerId as string)
        ?? '';
      const amount = (context?.amount ?? (
        serverAmount.value !== null ? serverAmount.value / 100 : totalWithFees.value
      )).toFixed(2);
      const orderId = checkoutOrderId.value;
      const success = failed ? 'false' : 'true';

      if (isAndroid) {
        const encodedBrowserFallbackUrl = encodeURIComponent(
          `${browserFallbackUrlBase}/${browserFallbackUrlPath}`,
        );
        let intentUrl = `intent://${appHost}/?action=purchaseTickets&orderId=${orderId}&stickerId=${stickerId}&amount=${amount}&success=${success}`;
        intentUrl += `#Intent;scheme=${appId};package=${appId};S.browser_fallback_url=${encodedBrowserFallbackUrl};end`;
        window.location.href = intentUrl;
      } else {
        // iOS
        window.location.href = `${getRedirectIOSUrl()}?action=purchaseTickets&orderId=${orderId}&stickerId=${stickerId}&amount=${amount}&success=${success}`;
      }

      // Safari redirect handling
      let didSafariRedirect = false;
      if (isSafari()) {
        didSafariRedirect = await handleSafariPrompt();
      }

      // Check if redirect was successful
      if (!isSafari() || didSafariRedirect !== false) {
        setTimeout(() => {
          if (document.hidden) {
            window.location.href = `${browserFallbackUrlBase}/${browserFallbackUrlPath}`;
            loadingInstance.close();
          } else {
            loadingInstance.close();
          }
        }, 1500);
      } else {
        loadingInstance.close();
      }
    }

    function goBack() {
      if (isEmbeddedContext()) {
        const navigated = navigateToEmbedOrganizationHome(route, undefined, { router });
        if (navigated) {
          return;
        }
      }

      router.back();
    }

    async function clearCart() {
      try {
        await ElMessageBox.confirm(
          'Are you sure you want to remove all items from your cart?',
          'Clear Cart',
          {
            type: 'warning',
            confirmButtonText: 'Clear Cart',
            cancelButtonText: 'Keep Items',
          },
        );
      } catch {
        return;
      }

      store.dispatch('cart/clearCart');
      selectedCreatorId.value = null;
      paymentMounted.value = false;
      paymentReady.value = false;
      serverAmount.value = null;
      stripeError.value = null;

      webAppToast.success('Cart cleared');
    }

    function removeLine(lineId: string) {
      store.dispatch('cart/removeLine', lineId);

      webAppToast.info('Item removed from cart');
    }

    const maxCartQuantity = 99;

    function canAdjustQuantity(line: CartLine): boolean {
      return line.lineType === 'Ticket' || line.lineType === 'Sticker';
    }

    function getLineQuantity(line: CartLine): number {
      return line.quantity ?? 1;
    }

    function incrementLineQuantity(line: CartLine) {
      const current = getLineQuantity(line);
      if (current >= maxCartQuantity) return;
      store.dispatch('cart/updateLine', { ...line, quantity: current + 1 });
    }

    function decrementLineQuantity(line: CartLine) {
      const current = getLineQuantity(line);
      if (current <= 1) {
        removeLine(line.id);
        return;
      }
      store.dispatch('cart/updateLine', { ...line, quantity: current - 1 });
    }

    function getGroupItemCount(group: CartCreatorGroup): number {
      return group.lines.reduce((sum, line) => sum + (line.quantity ?? 1), 0);
    }

    onMounted(async () => {
      if (hasPaymentIntent) {
        const pubkey = route.query.pubkey as string;
        const clientSecret = route.query.client_secret as string;
        const amountCents = parseInt(route.query.amount as string, 10);

        serverAmount.value = amountCents;
        checkoutOrderId.value = cartId || '';

        if (route.query.email) {
          contactInfo.email = decodeURIComponent(route.query.email as string);
          saveGuestCheckoutContact(contactInfo.email, contactInfo.phone || '');
        }

        if (isFree) {
          paymentMounted.value = false;
        } else {
          try {
            await loadStripeAndMount(pubkey, clientSecret);
          } catch (err) {
            console.error('[Checkout] Failed to load Stripe:', err);
            pageError.value = 'Failed to load payment form. Please try again.';
          }
        }
      } else if (!isEmptyCart.value && me.value?.token) {
        await fetchWalletBalance();
      }
    });

    return {
      layoutComponent,
      lines,
      creatorGroups,
      contactInfo,
      contactRules,
      contactFormRef,
      isAuthenticated,
      hasPaymentIntent,
      isEmptyCart,
      subtotal,
      fees,
      totalWithFees,
      hasWallet,
      useGivingWallet,
      donationTotal,
      walletEligibleTotal,
      maxWalletCredit,
      givingWalletAmount,
      chargeAfterWallet,
      optInNotifications,
      serverAmount,
      loading,
      pageError,
      paymentMounted,
      paymentReady,
      initiateCheckout,
      confirmPayment,
      goBack,
      clearCart,
      removeLine,
      canAdjustQuantity,
      getLineQuantity,
      incrementLineQuantity,
      decrementLineQuantity,
      getGroupItemCount,
      maxCartQuantity,
      router,
      selectedCreatorId,
      calculateGroupSubtotal,
      selectCreatorForCheckout,
    };
  },
});
</script>

<style lang="scss" scoped>
.ticket-checkout-page {
  min-height: 100vh;
  background: #f5f5f7;
  padding: 1.5rem 1rem 4rem;

  &.has-checkout-footer {
    padding-bottom: 9.5rem;
  }

  &.is-empty-cart {
    display: flex;
    flex-direction: column;
    min-height: calc(100vh - 5rem);
    padding: 1rem;
  }
}

.checkout-container {
  max-width: 560px;
  margin: 0 auto;

  &--empty {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
  }
}

.checkout-section {
  background: #fff;
  border-radius: 12px;
  padding: 1.25rem 1.5rem;
  margin-bottom: 1rem;
}

.contact-section,
.order-summary-section,
.payment-section {
  scroll-margin-top: 1rem;
  scroll-margin-bottom: 10rem;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.section-title {
  font-size: 1rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 1rem;
}

.section-header .section-title {
  margin: 0;
}

.clear-cart-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.65rem;
  border: 1px solid #f5c6cb;
  border-radius: 6px;
  background: #fff5f5;
  color: #c0392b;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.2s, border-color 0.2s;

  img {
    width: 1rem;
    height: 1rem;
    flex-shrink: 0;
  }

  &:hover {
    background: #ffe8e8;
    border-color: #f1aeb5;
  }
}

.creator-header {
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #f0f0f0;

  .creator-name {
    font-size: 0.9rem;
    font-weight: 600;
    color: #555;
  }
}

.line-item {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f9f9f9;

  &:last-child {
    border-bottom: none;
  }
}

.line-item-info {
  flex: 1;

  .line-title {
    font-size: 0.95rem;
    font-weight: 500;
    color: #1a1a1a;
  }

  .line-option {
    font-size: 0.8rem;
    color: #888;
    margin-top: 0.15rem;
  }
}

.line-item-right {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.75rem;
  position: relative;
  padding-right: 1.75rem;

  .line-price {
    font-size: 0.95rem;
    font-weight: 600;
    color: #1a1a1a;
    white-space: nowrap;
  }

  .remove-btn {
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: background 0.2s;

    &:hover {
      background: rgba(255, 0, 0, 0.1);
    }

    img {
      width: 14px;
      height: 14px;
      opacity: 0.5;
    }
  }
}

.qty-stepper {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex-shrink: 0;
}

.qty-btn {
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 9999px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: #f3f4f6;
  transition: background 0.2s;

  img {
    width: 0.75rem;
    height: 0.75rem;
  }

  &.plus {
    background: #1fbeaf;

    img {
      filter: brightness(0) invert(1);
    }

    &:hover:not(:disabled) {
      background: rgba(31, 190, 175, 0.9);
    }
  }

  &:hover:not(:disabled) {
    background: #e5e7eb;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.qty-value {
  font-size: 0.875rem;
  font-weight: 600;
  min-width: 1.25rem;
  text-align: center;
  color: #1a1a1a;
}

.form-row {
  display: flex;
  gap: 1rem;

  .form-col {
    flex: 1;
  }
}

.wallet-section {
  .wallet-toggle {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .wallet-note {
    font-size: 0.85rem;
    color: #555;
    padding-left: 1.5rem;
  }

  .wallet-info-text {
    font-size: 0.8rem;
    color: #666;
    margin-top: 0.5rem;
    padding-left: 1.5rem;
  }
}

.fee-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.35rem 0;
  font-size: 0.9rem;
  color: #444;
  border-bottom: 1px solid #f5f5f5;

  &:last-child {
    border-bottom: none;
  }

  &.fee-total {
    font-size: 1rem;
    font-weight: 700;
    color: #1a1a1a;
    padding-top: 0.65rem;
    margin-top: 0.25rem;
    border-top: 2px solid #e5e5e5;
    border-bottom: none;
  }
}

.checkout-footer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #fff;
  border-top: 1px solid #e5e7eb;
  padding: 1rem 1rem 0.75rem;
  z-index: 40;
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.08);

  @media (min-width: 1024px) {
    left: 300px;
    width: calc(100% - 300px);
  }
}

.checkout-footer-inner {
  width: 100%;
  max-width: 560px;
  margin: 0 auto;
  display: flex;
  justify-content: center;

  @media (min-width: 1024px) {
    max-width: 1100px;
    margin-left: 0;
    margin-right: auto;
  }
}

.checkout-footer-actions {
  width: 100%;
  max-width: 360px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.65rem;

  :deep(.checkout-btn.el-button) {
    width: 100%;
    height: 52px;
    margin: 0;
    padding-left: 1.5rem;
    padding-right: 1.5rem;
    font-size: 1rem;
    font-weight: 700;
    border-radius: 9999px;
    border: none;
    background: linear-gradient(90deg, #0d9488 0%, #14b8a6 100%) !important;
    box-shadow: 0 8px 24px rgba(13, 148, 136, 0.3);

    .el-button__content,
    > span {
      display: inline-flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      text-align: center;
    }

    &:hover:not(:disabled) {
      background: linear-gradient(90deg, #0f766e 0%, #0d9488 100%) !important;
      box-shadow: 0 10px 28px rgba(13, 148, 136, 0.35);
    }

    &.is-loading {
      .el-icon {
        position: absolute;
        left: 1.25rem;
      }
    }
  }
}

.cancel-btn {
  width: 100%;
  border: none;
  background: transparent;
  color: #888;
  font-size: 0.9rem;
  padding: 0.15rem 0 0;
  cursor: pointer;
  text-align: center;
}

.optin-section {
  padding-top: 1rem;
  padding-bottom: 1rem;
}

.page-error {
  text-align: center;
  padding: 3rem 1rem;
  color: #c0392b;
}

#payment-element {
  margin-top: 0.5rem;
  min-height: 200px;
}

// Multi-creator cart UI
.multi-creator-notice {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: #fff4e6;
  border: 1px solid #ffa940;
  border-radius: 8px;
  margin-bottom: 1rem;

  .notice-icon {
    width: 1.25rem;
    height: 1.25rem;
    opacity: 0.8;
  }

  .notice-text {
    font-size: 0.85rem;
    color: #874d00;
    font-weight: 500;
  }
}

.creator-groups {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.creator-group {
  border: 2px solid #e5e5e5;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.2s;

  &.is-selectable {
    cursor: pointer;

    &:hover {
      border-color: #5eead4;
    }
  }

  &.active {
    border-color: #0d9488;
    box-shadow: 0 0 0 2px rgba(13, 148, 136, 0.15);
  }
}

.creator-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  background: #fafafa;
  border-bottom: 1px solid #e5e5e5;
}

.creator-info {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  min-width: 0;

  .creator-checkbox {
    width: 1.25rem;
    height: 1.25rem;
    border-radius: 0.4rem;
    border: 2px solid #cbd5e1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: #fff;
    transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;

    &.checked {
      border-color: transparent;
      background: linear-gradient(90deg, #0d9488 0%, #14b8a6 100%);
      box-shadow: 0 2px 8px rgba(13, 148, 136, 0.35);
    }

    img {
      width: 0.75rem;
      height: 0.75rem;
      filter: brightness(0) invert(1);
    }
  }

  .creator-name {
    font-size: 0.9rem;
    font-weight: 600;
    color: #1a1a1a;
  }
}

.creator-item-count {
  font-size: 0.8rem;
  color: #888;
  font-weight: 500;
}

.creator-items {
  padding: 1rem;
  padding-right: 3rem;
  background: white;
  position: relative;
}

.creator-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: #fafafa;
  border-top: 1px solid #e5e5e5;
}

.creator-subtotal {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;

  .subtotal-label {
    font-size: 0.75rem;
    color: #888;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .subtotal-amount {
    font-size: 1.1rem;
    font-weight: 700;
    color: #1a1a1a;
  }
}

.empty-cart-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background: #fff;
  border-radius: 12px;
  padding: 3rem 2rem;
  width: 100%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);

  .empty-cart-icon img {
    width: 4rem;
    height: 4rem;
    opacity: 0.35;
    margin-bottom: 1.25rem;
  }

  .empty-cart-title {
    font-size: 1.25rem;
    font-weight: 700;
    color: #1a1a1a;
    margin: 0 0 0.5rem;
  }

  .empty-cart-text {
    font-size: 0.95rem;
    color: #888;
    margin: 0 0 1.5rem;
    max-width: 280px;
  }

  .browse-btn {
    min-width: 200px;
  }
}
</style>
